<template>
    <div class="relative business_permit_modal z-50" aria-labelledby="modal-title" role="dialog" aria-modal="true"
        v-if="amountopen == true">
        <!-- Overlay with blur effect -->
        <div class="fixed inset-0 bg-gray-900/75 backdrop-blur-sm transition-opacity"></div>

        <div class="fixed inset-0 z-10 overflow-y-auto">
            <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                <div
                    class="relative transform overflow-hidden rounded-xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                    <!-- Modal Header -->
                    <div class="bg-gradient-to-r from-orange-600 to-orange-700 px-6 py-4">
                        <div class="flex items-center justify-between">
                            <div class="flex flex-col text-white">
                                <h3 class="text-xl font-semibold flex items-center gap-2">
                                    <i class="fas fa-money-bill-wave h-5 w-5" aria-hidden="true">
                                    </i>
                                    Update Amount
                                </h3>
                                <p class="text-sm text-orange-100 mt-1"> <i class="fas fa-project-diagram  mr-2"></i>{{
                                    project_name ||
                                    'Project Details' }}
                                </p>
                            </div>
                            <button type="button" @click="update_amount_popup(false)"
                                class="text-white hover:text-orange-100 focus:outline-none">
                                <span class="sr-only">Close</span>
                                <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>
                    </div>

                    <!-- Modal Body -->
                    <form method="post" @submit.prevent="update" class="px-6 py-4">
                        <div class="grid grid-cols-1 gap-y-4">
                            <div class="mb-2">
                                <select-input v-model="form.type" :error="form.errors.type" label="Type"
                                    required="true">
                                    <template #icon>
                                        <font-awesome-icon :icon="['fas', 'calculator']"
                                            class="h-5 w-5 text-gray-400" />
                                    </template>
                                    <option :value="null">Select Type</option>
                                    <option :value="1">Credit</option>
                                    <option :value="2">Debit</option>
                                </select-input>
                            </div>

                            <div class="mb-2">
                                <text-input v-model="form.mooe_amount" :error="form.errors.mooe_amount"
                                    label="Project MOOE Amount" placeholder="Enter MOOE amount" type="text"
                                    pattern="^\d*\.?\d+$" min="0">
                                    <template #icon>
                                        <font-awesome-icon :icon="['fas', 'coins']" class="h-5 w-5 text-gray-400" />
                                    </template>
                                </text-input>
                            </div>

                            <div class="mb-2">
                                <text-input v-model="form.co_amount" :error="form.errors.co_amount"
                                    label="Project CO Amount" placeholder="Enter CO amount" type="text"
                                    pattern="^\d*\.?\d+$" min="0">
                                    <template #icon>
                                        <i class="fas fa-coins h-5 w-5 text-gray-400"></i>
                                    </template>
                                </text-input>
                            </div>

                            <div class="mb-2">
                                <text-input required v-model="form.project_amount" :error="form.errors.project_amount"
                                    label="Total Project Amount" placeholder="Total amount" type="text"
                                    pattern="^\d*\.?\d+$" min="0" disabled>
                                    <template #icon>
                                        <i class="fas fa-calculator h-5 w-5 text-gray-400"></i>
                                    </template>
                                </text-input>
                            </div>
                        </div>

                        <!-- Modal Footer -->
                        <div class="mt-6 flex justify-end gap-3">
                            <button type="button"
                                class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                @click="update_amount_popup(false)">
                                <i class="fas fa-times mr-2"></i>
                                Cancel
                            </button>
                            <button type="submit"
                                class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500">
                                <i class="fas fa-check mr-2"></i>
                                Update Amount
                            </button>
                        </div>
                    </form>
                </div>
            </div>

        </div>
    </div>
</template>
<script setup>
    import axios from 'axios';
    import { ref, toRefs, inject, onMounted, watch } from 'vue'
    import { Head, Link, useForm } from '@inertiajs/inertia-vue3'
    import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
    import { XMarkIcon } from '@heroicons/vue/24/outline'
    import Button from '@/Shared/Button'
    import TextInput from '@/Shared/TextInput'
    import SelectInput from "@/Shared/SelectInput";

    const props = defineProps(['amountopen', 'project_id', 'project_name'])
    const backendPrefix = ref(process.env.MIX_ADMIN_PREFIX)
    const edit = ref(false)
    const swal = inject('$swal')
    const preloader = ref(false)
    const message = ref('');
    const url = ref('/backend/projects/amount-update')
    const emit = defineEmits(['UpdateAmountPopup'])
    const update_amount_popup = (status) => {
        emit('UpdateAmountPopup', status)
    }

    const form = useForm({
        project_id: null,
        type: null,
        project_amount: null,
        mooe_amount: 0,
        co_amount: 0,
    });

    const update = () => {
        form.project_id = props.project_id
        swal({
            title: 'Are you sure?',
            text: "You want to proceed with this action",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await axios.put(`${url.value}/${form.project_id}`, {
                        type: form.type,
                        project_amount: form.project_amount,
                        mooe_amount: form.mooe_amount,
                        co_amount: form.co_amount,
                        project_id: form.project_id
                    });

                    if (response.data.status === 'success') {
                        swal.fire(
                            'Updated!',
                            response.data.message,
                            'success'
                        );

                        // Reset form values
                        form.reset();
                        form.type = null;
                        form.project_amount = null;
                        form.mooe_amount = 0;
                        form.co_amount = 0;
                        emit('UpdateAmountPopup', false);

                        // Handle redirect if provided
                        const redirectUrl = response.headers['x-redirect-url'];
                        if (redirectUrl) {
                            window.location.href = redirectUrl;
                        }
                    }
                } catch (error) {
                    const errorMessage = error.response?.data?.message || 'An error occurred while updating the amount';

                    if (error.response?.data?.error === "Insufficient remaining project amount") {
                        swal.fire(
                            'Error!',
                            'Insufficient remaining project amount',
                            'error'
                        );
                    } else if (error.response?.status === 422) {
                        // Validation errors
                        swal.fire(
                            'Validation Error!',
                            errorMessage,
                            'error'
                        );
                    } else {
                        swal.fire(
                            'Error!',
                            errorMessage,
                            'error'
                        );
                    }
                }
            }
        });
    }

    const formatNumber = (num) => {
        return parseFloat(num)
    }
    watch(() => [form.mooe_amount, form.co_amount], ([newA, newB], [prevA, prevB]) => {
        form.project_amount = (formatNumber(Number(form.mooe_amount)) + formatNumber(Number(form.co_amount)));
    }, { deep: true }
    );
</script>
